import { DokumentTypMappingProtokollAction } from '@core/domain';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import {
  Check,
  PauseIcon,
  RocketIcon,
  RotateCcw as Restore,
  Trash2,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useAktivierePausiereDokumenttyp,
  useDeleteDokumenttyp,
  useEventDokumentTypMappingByExternalId,
  useEventDokumentTypProtocolByExternalId,
  useRestoreEventDokumentTypByExternalId,
  useUpdateDokumenttyp,
} from '@/adapter/opm-cockpit-backend/useEventDokumentTypMappings';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import {
  TimelineInitiator,
  TimelineTime,
} from '@/components/ui/timeline/timeline';
import { getErrResponseDetail, getOnSaveErrorMessage } from '@/lib/utils';
import { AnmerkungenBox } from '@/pages/Konfiguration/DokumentTypen/components/AnmerkungenBox';
import {
  CreateMappingRulesBox,
  mappingValuesToPayload,
} from '@/pages/Konfiguration/DokumentTypen/components/CreateMappingRulesBox';
import { DokumentTypBaseFieldsBox } from '@/pages/Konfiguration/DokumentTypen/components/DokumentTypBaseFieldsBox';
import {
  DEFAULT_DOKUMENTTYP_FORM_VALUES,
  DokumentTypFormInput,
  dokumentTypErstellenSchema,
} from '@/pages/Konfiguration/DokumentTypen/schemas/dokumenttypenerstellen';
import { ErrorMessage } from '@/pages/VorgangBearbeitung/components';
import { ActionButton } from '@/pages/VorgangBearbeitung/components/ActionButton';

export const DOKUMENT_TYP_PROTOKOLL_ACTIONS_TRANSLATION_MAP = {
  [DokumentTypMappingProtokollAction.ARCHIVIERUNG_ACTIVATED]:
    'Archivierung wurde aktiviert.',
  [DokumentTypMappingProtokollAction.ARCHIVIERUNG_DEACTIVATED]:
    'Archivierung wurde deaktiviert.',
  [DokumentTypMappingProtokollAction.ANMERKUNG_ACTIVATED]:
    'Anmerkungen wurden aktiviert.',
  [DokumentTypMappingProtokollAction.ANMERKUNG_DEACTIVATED]:
    'Anmerkungen wurden deaktiviert.',
  [DokumentTypMappingProtokollAction.ANMERKUNG_BETREFF_UPDATED]:
    'Betreff der Anmerkung wurde geändert.',
  [DokumentTypMappingProtokollAction.ANMERKUNG_TEXT_UPDATED]:
    'Anmerkungstext wurde geändert.',
  [DokumentTypMappingProtokollAction.ANMERKUNG_DELETED]:
    'Betreff und Anmerkungstext wurden durch Deaktivierung gelöscht.',
  [DokumentTypMappingProtokollAction.CREATED]:
    'Das Dokumenttyp Mapping wurde erstellt.',
  [DokumentTypMappingProtokollAction.UPDATED]:
    'Das Dokumenttyp Mapping wurde verändert.',
  [DokumentTypMappingProtokollAction.ACTIVATED]:
    'Das Dokumenttyp Mapping wurde aktiviert.',
  [DokumentTypMappingProtokollAction.DEACTIVATED]:
    'Das Dokumenttyp Mapping wurde deaktiviert.',
  [DokumentTypMappingProtokollAction.DELETED]:
    'Das Dokumenttyp Mapping wurde gelöscht.',
  [DokumentTypMappingProtokollAction.RESTORED]:
    'Das Dokumenttyp Mapping wurde wiederhergestellt.',
};

export const EditDokumentTypPage: React.FC = () => {
  const { externalId } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, error } =
    useEventDokumentTypMappingByExternalId(externalId);
  const updateDokumenttyp = useUpdateDokumenttyp();
  const deleteDokumenttyp = useDeleteDokumenttyp();
  const restoreDokumenttyp = useRestoreEventDokumentTypByExternalId();
  const aktivierePausiereDokumenttyp = useAktivierePausiereDokumenttyp();
  const [err, setErr] = useState<unknown>();

  const handleToggleAktiviert = useCallback(
    (externalId: string, dokumentTyp: string, nextChecked: boolean) => {
      aktivierePausiereDokumenttyp.mutate({
        externalId: externalId,
        dokumentTyp: dokumentTyp,
        aktiviert: nextChecked,
      });
    },
    [],
  );

  const form = useForm<DokumentTypFormInput>({
    resolver: zodResolver(dokumentTypErstellenSchema),
    defaultValues: DEFAULT_DOKUMENTTYP_FORM_VALUES,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = form;

  const onSubmitUpdateDokumentTypMapping = (values: DokumentTypFormInput) => {
    updateDokumenttyp.mutate(
      {
        externalId: data.externalId,
        dokumentTyp: values.dokumentTyp,
        eventName: values.eventName,
        eventTyp: values.eventTyp,
        aktiviert: data.aktiviert,
        ...mappingValuesToPayload(values),
      },
      {
        onSuccess: () => {
          setErr(null);
          navigate('/konfiguration');
        },
        onError: (err: unknown) => {
          setErr(err);
        },
      },
    );
  };

  useEffect(() => {
    if (data) {
      // sync react-hook-form internal values with loaded data so initial submit validates
      reset({
        dokumentTyp: data.dokumentTyp || '',
        eventName: data.eventName || '',
        eventTyp: (data.eventTyp ||
          'order') as DokumentTypFormInput['eventTyp'],
        streamserveProjektExternalId:
          (data as { streamserveProjektExternalId?: string | null })
            .streamserveProjektExternalId ?? '',
        exstreamServiceName: data.exstreamServiceName ?? '',
        versandartUebersteuerung:
          (data.versandartUebersteuerung as DokumentTypFormInput['versandartUebersteuerung']) ??
          '',
        emailVersandArt:
          (data.emailVersandArt as DokumentTypFormInput['emailVersandArt']) ??
          'OHNE_DOKUMENT',
        emailKundenkommunikation: data.emailKundenkommunikation ?? null,
        archivierungAktiv: data.archivierungAktiv ?? true,
        anmerkungAktiv: data.anmerkungAktiv ?? false,
        anmerkungBetreff: data.anmerkungBetreff ?? '',
        anmerkungText: data.anmerkungText ?? '',
        prioVersand: data.prioVersand ?? false,
        beilagen: (data.beilagen ?? []).map((b) => ({
          id: b.id ?? '',
          label: b.label ?? '',
          druck: b.druck ?? false,
          email: b.email ?? false,
        })),
      });
    }
  }, [data, reset]);

  if (isLoading) return <div>Lade Dokumenttyp...</div>;
  if (error)
    return (
      <div className="text-destructive">
        Fehler beim Laden des Dokumenttyps.
      </div>
    );
  if (!data) return <div>Dokumenttyp nicht gefunden.</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-text-primary">
          Dokumenttyp bearbeiten
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmitUpdateDokumentTypMapping)}>
        <div className="bg-white border border-table-border rounded-md p-4">
          <DokumentTypBaseFieldsBox register={register} errors={errors} />
          <div className="pt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Status
              </span>
              <div className="w-full px-3 py-2 text-sm">
                <Badge variant={data.aktiviert ? 'success' : 'warning'}>
                  {data.aktiviert ? 'Aktiviert' : 'Pausiert'}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <CreateMappingRulesBox form={form} />
        </div>

        <div className="mt-4">
          <AnmerkungenBox form={form} />
        </div>

        <div className="md:flex md:items-start md:gap-6 mt-6">
          <div className="bg-white border border-table-border rounded-lg p-6 w-full md:w-4/9">
            <h2 className="text-lg font-semibold text-text-primary mb-4">
              Protokoll
            </h2>

            <ProtocolList externalId={externalId} />
          </div>

          <div
            className={`bg-white border border-table-border rounded-lg p-6 w-full md:w-5/9 ml-auto mt-6 md:mt-0`}
          >
            <h2 className="text-lg font-semibold text-text-primary mb-4">
              Aktionen
            </h2>

            <div className={`flex flex-wrap gap-3`}>
              <ActionButton
                variant="outline"
                onClick={() => navigate('/konfiguration')}
                loadingText="Zurück..."
                normalText="Abbrechen"
                icon={X}
                type={'reset'}
              />
              <ActionButton
                disabled={false}
                loading={updateDokumenttyp.isPending}
                loadingText="Wird geupdated..."
                normalText="Speichern"
                icon={Check}
                type="submit"
              />
              {!(data.deletedBy || data.deletedAt) && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <ActionButton
                      disabled={false}
                      loading={aktivierePausiereDokumenttyp.isPending}
                      loadingText="Wird aktiviert oder pausiert..."
                      normalText={data.aktiviert ? 'Pausieren' : 'Aktivieren'}
                      icon={data.aktiviert ? PauseIcon : RocketIcon}
                      className={
                        data.aktiviert
                          ? 'bg-status-warning hover:bg-status-warning'
                          : 'bg-blue-500 hover:bg-blue-600'
                      }
                    />
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Sind Sie wirklich sicher?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        Möchten Sie diesen Dokumenttypen wirklich{' '}
                        {data.aktiviert ? 'pausieren' : 'aktivieren'}? <br />
                        <br />
                        {data.aktiviert
                          ? 'Der Dokumenttyp wird damit pausiert und alle eingehenden ' +
                            'Vorgänge werden in den Status pausiert gesetzt und nicht verarbeitet. ' +
                            'Diese Aktion wird sofort ausgeführt, ein weiteres Speichern des ' +
                            'Dokumenttyp Mappings ist nicht notwendig.'
                          : 'Das Dokumenttyp Mapping wird damit aktiviert. Alle pausierten Vorgänge mit diesem' +
                            ' Dokumenttyp werden nach Aktivierung neu gestartet. ' +
                            'Diese Aktion wird sofort ausgeführt, ein weiteres Speichern des Dokumenttyp Mappings ist nicht notwendig.'}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => {
                          handleToggleAktiviert(
                            data.externalId,
                            data.dokumentTyp,
                            !data.aktiviert,
                          );
                        }}
                        className={
                          data.aktiviert
                            ? 'bg-status-warning hover:bg-status-warning'
                            : 'bg-blue-500 hover:bg-blue-600'
                        }
                      >
                        {data.aktiviert ? 'Pausieren' : 'Aktivieren'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              {!(data.deletedBy || data.deletedAt) && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <ActionButton
                      disabled={false}
                      loading={deleteDokumenttyp.isPending}
                      loadingText="Wird gelöscht..."
                      normalText="Löschen"
                      icon={Trash2}
                      variant="destructive"
                    />
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Sind Sie wirklich sicher?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        Gelöschte Dokumenttypen werden deaktiviert und als
                        gelöscht markiert. Vorgänge mit dem Event dieses
                        Mappings werden nicht mehr weiter verarbeitet und auf{' '}
                        <i>PAUSIERT</i> gesetzt.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => {
                          deleteDokumenttyp.mutate(data.externalId);
                          navigate('/konfiguration');
                        }}
                        className="bg-red-500 hover:bg-red-600 text-white"
                      >
                        Löschen
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              {(data.deletedBy || data.deletedAt) && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <ActionButton
                      disabled={false}
                      loading={restoreDokumenttyp.isPending}
                      loadingText="Wird wiederhergestellt..."
                      normalText="Wiederherstellen"
                      icon={Restore}
                      className={'bg-blue-500 hover:bg-blue-600'}
                    />
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Sind Sie wirklich sicher?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        Der Dokumenttyp wird deaktiviert wiederhergestellt und
                        muss manuell aktiviert werden. <br /> Wird der
                        Dokumenttyp wieder aktiviert, wird für alle Vorgänge mit
                        diesem Dokumenttyp die im Status <i>PAUSIERT</i> und im
                        Prozessschritt
                        <i> WARTE_AUF_REAKTIVIERUNG_DES_DOKUMENTTYPS</i> eine
                        neue Abarbeitung gestartet.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => {
                          restoreDokumenttyp.mutate(data.externalId);
                        }}
                        className="bg-green-500 hover:bg-green-600 text-white"
                      >
                        Wiederherstellen
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>

            <div className="mt-4 text-sm text-text-secondary">
              <p>
                <strong>Hinweise:</strong>
              </p>
              <ul className="list-disc list-inside space-y-1 mt-2">
                <li>
                  <strong>Abbrechen:</strong> Springt zurück zur Übersicht der
                  konfigurierten Dokumenttyp Mappings.
                </li>
                <li>
                  <strong>Speichern:</strong> Speichert die Änderungen am
                  Dokumenttyp Mapping.
                </li>
                {!(data.deletedBy || data.deletedAt) && data.aktiviert && (
                  <li>
                    <strong>Pausieren:</strong> Pausiert das Dokumenttyp
                    Mapping. Alle Vorgänge dieses Dokumenttyps werden nicht mehr
                    verarbeitet und in den Status PAUSIERT gesetzt.
                  </li>
                )}
                {!(data.deletedBy || data.deletedAt) && !data.aktiviert && (
                  <li>
                    <strong>Aktivieren:</strong> Aktiviert das Dokumenttyp
                    Mapping. Alle pausierten Vorgänge dieses Dokumenttyps werden
                    neu verarbeitet.
                  </li>
                )}
                {!(data.deletedBy || data.deletedAt) && (
                  <li>
                    <strong>Löschen:</strong> Deaktiviert das Dokumenttyp
                    Mapping und markiert diesen als gelöscht.
                  </li>
                )}
                {(data.deletedBy || data.deletedAt) && (
                  <li>
                    <strong>Wiederherstellen:</strong> Stellt das Dokumenttyp
                    Mapping deaktiviert wieder her. Um die in der Zwischenzeit
                    angefallenen Vorgänge für diesen Dokumenttyp zu starten,
                    muss das Dokumenttyp Mapping im Nachgang aktiviert werden.
                  </li>
                )}
              </ul>
            </div>
            {err && (
              <div className="mt-4">
                <ErrorMessage
                  errorMessage={getErrResponseDetail(err)}
                  errorList={getOnSaveErrorMessage(err)}
                />
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

const ProtocolList: React.FC<{ externalId: string }> = ({ externalId }) => {
  const { data, isLoading, error } =
    useEventDokumentTypProtocolByExternalId(externalId);

  if (isLoading) return <div>Lade Protokoll...</div>;
  if (error)
    return (
      <div className="text-destructive">Fehler beim Laden des Protokolls.</div>
    );
  if (!data || data.length === 0) return <div>Keine Protokolle vorhanden.</div>;

  return (
    <div className="space-y-3">
      {data.map((entry) => (
        <div
          key={`${entry.performedAt}-${entry.action}`}
          className="pl-3 relative border rounded bg-gray-50 p-2"
        >
          <div className="flex items-center gap-2 mb-1">
            <TimelineTime className="text-xs text-muted-foreground">
              {format(entry.performedAt, 'dd.MM.yyyy HH:mm:ss')}
            </TimelineTime>
            <TimelineInitiator variant="small">
              {entry.performedBy || 'system'}
            </TimelineInitiator>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {DOKUMENT_TYP_PROTOKOLL_ACTIONS_TRANSLATION_MAP[entry.action]}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
